import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { navLinks, modsNavLinks, siteConfig } from "../data/mock";
import { Menu, X, ArrowRight, Phone, Mail, MapPin } from "lucide-react";

// Resolve which division the current route belongs to.
const useDivision = () => {
  const { pathname } = useLocation();
  if (pathname.startsWith("/mods")) return "mods";
  return "catapult"; // default — covers /catapult/* and any catapult-only legacy routes
};

const homeFor = (division) =>
  division === "mods" ? "/mods" : "/catapult";

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const division = useDivision();
  const cfg = siteConfig[division];
  const links = division === "mods" ? modsNavLinks : navLinks;
  const isHome = location.pathname === homeFor(division);
  const ctaTarget = division === "mods" ? "/mods#investors" : "/catapult/journey";
  const ctaLabel = division === "mods" ? "Investor Partnerships" : "Start Your Journey";

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled || !isHome
            ? "bg-white/95 backdrop-blur-md shadow-sm"
            : "bg-transparent"
        }`}
        data-testid={`header-${division}`}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20 md:h-24">
            <Link
              to={homeFor(division)}
              className="flex items-center gap-3 group whitespace-nowrap"
              data-testid="header-brand-link"
            >
              {division === "mods" && (
                <img
                  src="/logos/mods-symbol.png"
                  alt=""
                  className={`h-9 md:h-10 w-auto transition-all duration-500 ${
                    isScrolled || !isHome ? "opacity-100" : "opacity-100 invert"
                  }`}
                />
              )}
              <div className="flex items-baseline gap-2">
                <span
                  className={`font-display text-2xl md:text-[28px] font-semibold tracking-tight transition-colors duration-500 ${
                    isScrolled || !isHome ? "text-stone-900" : "text-white"
                  }`}
                >
                  {cfg.name}
                </span>
                <span
                  className={`font-body text-[11px] tracking-[0.2em] uppercase transition-colors duration-500 ${
                    isScrolled || !isHome ? "text-stone-400" : "text-white/60"
                  }`}
                >
                  {cfg.tagline}
                </span>
              </div>
            </Link>

            <nav className="hidden lg:flex items-center gap-10">
              {links.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`whitespace-nowrap font-body text-[13px] tracking-[0.08em] uppercase transition-colors duration-300 hover:opacity-100 ${
                    location.pathname + location.hash === link.path
                      ? isScrolled || !isHome
                        ? "text-stone-900 opacity-100"
                        : "text-white opacity-100"
                      : isScrolled || !isHome
                      ? "text-stone-500 opacity-80"
                      : "text-white/70 opacity-80"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to={ctaTarget}
                className="whitespace-nowrap font-body text-[13px] tracking-[0.08em] uppercase px-6 py-2.5 bg-stone-900 text-white rounded-none hover:bg-stone-800 transition-colors duration-300"
                data-testid="header-cta-button"
              >
                {ctaLabel}
              </Link>
            </nav>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 transition-colors duration-300 ${
                isScrolled || !isHome ? "text-stone-900" : "text-white"
              }`}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-40 bg-white transition-all duration-500 lg:hidden ${
          isMobileMenuOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="flex flex-col justify-center items-center h-full gap-8">
          {links.map((link, i) => (
            <Link
              key={link.path}
              to={link.path}
              className="font-body text-lg tracking-[0.12em] uppercase text-stone-600 hover:text-stone-900 transition-colors duration-300"
              style={{
                transitionDelay: `${i * 50}ms`,
                transform: isMobileMenuOpen ? "translateY(0)" : "translateY(20px)",
                opacity: isMobileMenuOpen ? 1 : 0,
                transition: "all 0.4s ease",
              }}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to={ctaTarget}
            className="mt-4 font-body text-sm tracking-[0.12em] uppercase px-8 py-3 bg-stone-900 text-white hover:bg-stone-800 transition-colors duration-300"
          >
            {ctaLabel}
          </Link>
          <Link
            to={division === "mods" ? "/catapult" : "/mods"}
            className="mt-2 font-body text-[11px] tracking-[0.2em] uppercase text-stone-400 hover:text-stone-700 transition-colors duration-300"
          >
            ↗ {division === "mods" ? "Catapult Custom Homes" : "Mods Enterprises Rentals"}
          </Link>
        </div>
      </div>
    </>
  );
};

export const Footer = () => {
  const division = useDivision();
  const cfg = siteConfig[division];
  const links = division === "mods" ? modsNavLinks : navLinks;
  const ctaTarget = division === "mods" ? "/mods#investors" : "/catapult/journey";
  const ctaCopy =
    division === "mods"
      ? {
          headline: "Interested in partnering on a development?",
          body: "Investor inquiries — let's start a conversation.",
          button: "Investor Partnerships",
        }
      : {
          headline: "Ready to build something extraordinary?",
          body: "Let's start a conversation about your future home.",
          button: "Begin Your Journey",
        };

  return (
    <footer className="bg-stone-950 text-white" data-testid={`footer-${division}`}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Top */}
        <div className="pt-20 pb-16 border-b border-white/10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            <div className="lg:col-span-2">
              <Link to={homeFor(division)} className="inline-flex items-center gap-3 mb-6">
                {division === "mods" && (
                  <img
                    src="/logos/mods-symbol.png"
                    alt=""
                    className="h-12 w-auto invert"
                  />
                )}
                <div>
                  <span className="font-display text-3xl font-semibold tracking-tight text-white block">
                    {cfg.name}
                  </span>
                  <span className="block font-body text-[11px] tracking-[0.25em] uppercase text-white/40 mt-1">
                    {cfg.tagline}
                  </span>
                </div>
              </Link>
              <p className="font-body text-white/50 text-sm leading-relaxed max-w-md">
                {cfg.description}
              </p>
              {/* sister division link */}
              <Link
                to={division === "mods" ? "/catapult" : "/mods"}
                className="inline-flex items-center gap-2 mt-6 font-body text-[11px] tracking-[0.2em] uppercase text-white/40 hover:text-white/80 transition-colors duration-300"
                data-testid="sister-division-link"
              >
                ↗ {division === "mods" ? "Catapult Custom Homes" : "Mods Enterprises Rentals"}
              </Link>
            </div>

            <div>
              <h4 className="font-body text-[11px] tracking-[0.2em] uppercase text-white/40 mb-6">
                Explore
              </h4>
              <div className="flex flex-col gap-3">
                {links.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="font-body text-sm text-white/60 hover:text-white transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-body text-[11px] tracking-[0.2em] uppercase text-white/40 mb-6">
                Connect
              </h4>
              <div className="flex flex-col gap-4">
                {division === "mods" && (
                  <>
                    <a
                      href={`tel:${cfg.phone}`}
                      className="flex items-center gap-3 font-body text-sm text-white/60 hover:text-white transition-colors duration-300"
                    >
                      <Phone size={14} strokeWidth={1.5} />
                      {cfg.phone}
                    </a>
                    <a
                      href={`mailto:${cfg.email}`}
                      className="flex items-center gap-3 font-body text-sm text-white/60 hover:text-white transition-colors duration-300"
                    >
                      <Mail size={14} strokeWidth={1.5} />
                      {cfg.email}
                    </a>
                  </>
                )}
                <div className="flex items-start gap-3 font-body text-sm text-white/60">
                  <MapPin size={14} strokeWidth={1.5} className="mt-0.5 shrink-0" />
                  {cfg.address}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Banner */}
        <div className="py-16 border-b border-white/10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h3 className="font-display text-2xl md:text-3xl text-white mb-2">
                {ctaCopy.headline}
              </h3>
              <p className="font-body text-white/50 text-sm">{ctaCopy.body}</p>
            </div>
            <Link
              to={ctaTarget}
              className="inline-flex items-center gap-3 font-body text-sm tracking-[0.08em] uppercase px-8 py-3.5 border border-white/30 text-white hover:bg-white hover:text-stone-900 transition-all duration-300 group"
              data-testid="footer-cta-button"
            >
              {ctaCopy.button}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </div>
        </div>

        {/* Bottom */}
        <div className="py-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-body text-[11px] tracking-[0.08em] text-white/30">
            &copy; {new Date().getFullYear()} {siteConfig.parent.full}. All rights reserved.
            <span className="hidden md:inline"> · </span>
            <span className="block md:inline italic">
              {division === "mods"
                ? "Catapult Development & Design is a sister division."
                : "Catapult Development & Design is a division of Mods Enterprises, LLC."}
            </span>
          </p>
          <div className="flex items-center gap-6">
            <a href="#privacy" className="font-body text-[11px] tracking-[0.08em] text-white/30 hover:text-white/60 transition-colors duration-300">
              Privacy Policy
            </a>
            <a href="#terms" className="font-body text-[11px] tracking-[0.08em] text-white/30 hover:text-white/60 transition-colors duration-300">
              Terms
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export const PageTransition = ({ children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsVisible(false);
    const timer = setTimeout(() => setIsVisible(true), 50);
    // Only auto-scroll-top when there's no anchor target
    if (!location.hash) {
      window.scrollTo(0, 0);
    }
    return () => clearTimeout(timer);
  }, [location]);

  return (
    <div
      className={`transition-all duration-700 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      }`}
    >
      {children}
    </div>
  );
};
